import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-100">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1711098256574-7b497260cdc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMHN0b25lJTIwcGF0aW8lMjBnYXJkZW4lMjBsdXh1cnl8ZW58MHx8fHwxNzg1NDc5NDU3fDA&ixlib=rb-4.1.0&q=85" alt="Patio" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative p-12 h-full flex flex-col justify-end text-white">
          <div className="inline-flex bg-white rounded-xl p-3 mb-5 w-fit">
            <img src="/logo.png" alt="Speedex International" className="h-12 w-auto" />
          </div>
          <h2 className="font-display font-black text-3xl max-w-sm leading-tight">Manage your leads, gallery & content from anywhere.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden inline-flex bg-white border border-slate-200 rounded-xl p-2.5 mb-8">
            <img src="/logo.png" alt="Speedex International" className="h-9 w-auto" />
          </div>
          <h1 className="font-display font-black text-slate-900 text-3xl">Welcome back</h1>
          <p className="text-slate-500 mt-2">Sign in to your admin panel.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="info@speedexgardencare.co.uk" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3.5 font-bold text-white hover:bg-green-700 transition-colors disabled:opacity-60" data-testid="login-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </button>
          </form>
          <div className="mt-5 flex items-center justify-between text-sm">
            <Link to="/admin/forgot-password" className="text-green-700 font-semibold hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            <Link to="/" className="text-slate-500 hover:text-slate-700">← Back to site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
